import React from 'react';
import { motion } from 'framer-motion';
import './About.css';

// Using emoji as fallback icons
const icons = {
  spice: '🌶️',
  farm: '🌱',
  culture: '🏛️'
};

// Enhanced content data in German
const aboutContent = {
  
  hero: {
    title: "Unsere kulinarische Reise",
    subtitle: "Wo jahrhundertealte Tradition auf moderne Innovation trifft",
    
    backgroundImage: "https://images.unsplash.com/photo-1547592180-85f173990554?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
  },
  introduction: {
    title: "Willkommen bei Papadam",
    paragraphs: [
  "Neu eröffnet Papadam zu einem beliebten Ziel für authentische indische Küche mit modernem Twist geworden.",
  "Unsere Köche verbinden traditionelle Techniken mit moderner Kochkunst und kreieren Gerichte, die Tradition ehren und moderne Gaumen begeistern.",
  "Wir beziehen unsere Gewürze direkt von Farmen in Indien und arbeiten mit lokalen Produzenten für frischeste Zutaten zusammen.",
  "Bei Papadam glauben wir, dass jede Mahlzeit eine Feier sein sollte - des Geschmacks, der Gemeinschaft und des reichen kulinarischen Erbes Indiens."
],

 images : [
  {
    url: "/images/p16.jpg",
    alt: "Indische Gewürze"
  },
  {
    url: "/images/p15.jpg",
    alt: "Tandoori-Küche"
  }
],


    stats: [
      { value: "200+", label: "Rezepte in unserem Repertoire" },
      { value: "15+", label: "Jahre kulinarische Exzellenz" },
      { value: "50+", label: "Direkt importierte Gewürze" }
    ]
  },
  features: {
    title: "Das Papadam-Erlebnis",
    items: [
      {
        icon: <span className="icon-emoji">{icons.spice}</span>,
        title: "Authentische Gewürze",
        description: "Wir importieren über 50 Gewürze direkt aus Indien für authentische Aromen in jedem Gericht."
      },
      {
        icon: <span className="icon-emoji">{icons.farm}</span>,
        title: "Frische Zutaten",
        description: "Tägliche Lieferungen von lokalen Bauern garantieren frischeste Produkte in jeder Mahlzeit."
      },
      {
        icon: <span className="icon-emoji">{icons.culture}</span>,
        title: "Kulturelles Erbe",
        description: "Jedes Rezept erzählt eine Geschichte von Indiens vielfältigen kulinarischen Traditionen."
      }
    ]
  }
};

// Reusable components
const HeroSection = ({ title, subtitle, backgroundImage }) => (
  <motion.div 
    className="about-hero"
    style={{ backgroundImage: `url(${backgroundImage})` }}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.8 }}
  >
    <div className="hero-content">
      <motion.h1
        initial={{ y: -20 }}
        animate={{ y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {title}
      </motion.h1>
      <motion.p
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        transition={{ delay: 0.4 }}
      >
        {subtitle}
      </motion.p>
    </div>
  </motion.div>
);

const FeatureCard = ({ icon, title, description }) => (
  <motion.div 
    className="feature-card"
    whileHover={{ y: -10 }}
    transition={{ duration: 0.3 }}
  >
    <div className="feature-icon">{icon}</div>
    <h3>{title}</h3>
    <p>{description}</p>
  </motion.div>
);

const About = () => {
  return (
    <div className="about-page">
      <HeroSection 
        title={<span style={{ color: 'white' }}>{aboutContent.hero.title}</span>}
        subtitle={<span style={{ color: 'white' }}>{aboutContent.hero.subtitle}</span>}
        backgroundImage={aboutContent.hero.backgroundImage}
      />

      <div className="about-container">
        {/* Introduction Section */}
        <section className="intro-section">
          <motion.h2 
            className="section-header"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {aboutContent.introduction.title}
          </motion.h2>
          
          <div className="intro-content">
            <motion.div 
              className="intro-text"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {aboutContent.introduction.paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
              
            </motion.div>
            
            <motion.div 
              className="intro-images"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {aboutContent.introduction.images.map((image, index) => (
                <img 
                  key={index}
                  src={image.url} 
                  alt={image.alt} 
                  className={`intro-image-${index + 1}`}
                />
              ))}
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <motion.h2 
            className="section-header"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {aboutContent.features.title}
          </motion.h2>
          
          <motion.div 
            className="features-grid"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {aboutContent.features.items.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default About;
